import {
  BREAKPOINT,
  COLORS,
  TYPE_SIZE,
  getAudienceTypeColor,
} from "utils/styles";
import {
  AUDIENCE_TYPES,
  AUDIENCE_TYPES_ARRAY,
  getAudienceDescription,
  getAudienceSlug,
} from "posts/constants";
import styled from "styled-components";
import AudienceEmoji from "components/AudienceEmoji";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { childVariants, accesibleVariants } from "utils/motion";

const Wrapper = styled.div`
  text-align: left;
  ${({ noWrap }) =>
    noWrap
      ? `
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        overflow: scroll;
        -webkit-overflow-scrolling: touch;

        @media ${BREAKPOINT.M} {
          grid-template-columns: repeat(4, 35vw);
          grid-column-gap: 0.5rem;
        }
        @media ${BREAKPOINT.S} {
          grid-template-columns: repeat(4, 70vw);
        }
      `
      : `
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  @media ${BREAKPOINT.M} {
    grid-template-columns: repeat(2, 1fr);
    grid-column-gap: 0.5rem;
    grid-row-gap: 0.5rem;
  }
  @media ${BREAKPOINT.S} {
    grid-template-columns: 1fr;
    grid-row-gap: 1rem;
  }`};
`;
const CardContent = styled.span`
  display: block;
  position: absolute;
  height: 1.8rem;
  overflow: hidden;
  bottom: 0;
  transition: all 350ms ease-out;
  margin: 1rem;
  margin-top: 0;
  width: 80%;
  padding-bottom: 0.5rem;
  line-height: 1.25;
  border-radius: 10px;
`;
const CardTitle = styled.span.attrs({ className: "mabry-medium" })`
  display: block;
  z-index: 3;
  display: inline-block;
  padding: 0.25rem 0.5rem;
  font-size: ${TYPE_SIZE.S};
  border-radius: 10px;
  color: ${COLORS.BLACK};
`;
const CardBody = styled.span`
  display: block;
  position: relative;
  z-index: 3;
  opacity: 0;
  transition: all 350ms ease-out;
  padding: 0.25rem 0.5rem 0 0.5rem;
  color: ${COLORS.BLACK};
`;
const AudienceCardImage = styled.span`
  display: block;
`;
const AudienceCard = styled.a`
  display: block;
  width: 100%;
  height: 424px;
  position: relative;
  overflow: hidden;

  &:nth-child(1) {
    padding-top: 4vw;
    padding-right: 1vw;
  }
  &:nth-child(2) {
    padding-right: 1vw;
  }
  &:nth-child(3) {
    padding-top: 8vw;
    padding-right: 1vw;
  }
  &:nth-child(4) {
    padding-top: 2vw;
  }

  @media ${BREAKPOINT.M} {
    padding: 0 !important;
  }

  ${CardTitle} {
    background-color: ${COLORS.WHITE};
  }

  ${AudienceCardImage} {
    width: 100%;
    height: 100%;
    position: relative;
    border-radius: 15px;
    background-color: ${({ audience }) => getAudienceTypeColor(audience)};

    &::before {
      content: "";
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      transition: all 350ms ease-out;
      border-radius: 15px;
      background-position: center;
      background-size: cover;
      background-image: ${({ audience }) => {
        switch (audience) {
          case AUDIENCE_TYPES.PEOPLE_POWER:
            return "url(/images/profile-banners/people-power-2.png)";
          case AUDIENCE_TYPES.IF_YOU_SAY_SO:
            return "url(/images/profile-banners/if-you-say-so-1.png)";
          case AUDIENCE_TYPES.TOUGH_COOKIES:
            return "url(/images/profile-banners/tough-cookies-1.png)";
          case AUDIENCE_TYPES.DONT_TREAD_ON_ME:
            return "url(/images/profile-banners/dont-tread-on-me-2.png)";
        }
      }};
    }
  }
`;

const hoverVariant = {
  hover: {
    // y: -100,
    opacity: 1,
    height: "fit-content",
    originY: 1,
    background: "white",
  },
};

const cardBodyHoverVariant = {
  hover: {
    opacity: 1,
  },
};

export default function AudienceGrid(props) {
  const shouldReduceMotion = useReducedMotion();
  let variants = shouldReduceMotion ? accesibleVariants : childVariants;

  return (
    <Wrapper noWrap={props.noWrap}>
      {AUDIENCE_TYPES_ARRAY.map((audience, i) => {
        return (
          <Link
            key={`audience-${getAudienceSlug(audience)}`}
            href={`/audiences/${getAudienceSlug(audience)}/`}
            passHref
          >
            <AudienceCard as={motion.a} variants={variants} audience={audience}>
              <AudienceCardImage
                as={motion.span}
                whileHover="hover"
                whileFocus="hover"
              >
                <CardContent as={motion.div} variants={hoverVariant}>
                  <CardTitle>
                    <AudienceEmoji audience={audience} />
                    {audience}
                  </CardTitle>
                  <CardBody as={motion.span} variants={cardBodyHoverVariant}>
                    {getAudienceDescription(audience)}
                  </CardBody>
                </CardContent>
              </AudienceCardImage>
            </AudienceCard>
          </Link>
        );
      })}
    </Wrapper>
  );
}
